// Simple external store for cart data
interface CartItem {
  key: string;
  properties?: {
    custom_key?: string;
    is_custom_option?: boolean;
  };
}

interface CartState {
  items: CartItem[];
  isMiniCartOpen: boolean;
  isGiftPopupOpen: boolean;
  nvd_checkbox_state: boolean;
  nvd_checkbox_state_value: number;
  count?: number;
  total?: number;
  main_items_count?: number;
  main_items_total?: string;
  main_items_compared_total?: string;
  main_items_savings?: string;
  free_shipping_threshold?: string;
  free_shipping_below_threshold_message?: string;
  free_shipping_met_threshold_message?: string;
  nvd_opted_out?: number;
  recommendations_collection?: string;
  gift_threshold_1?: number;
  gift_threshold_2?: number;
  gift_threshold_3?: number;
  isAddingGift?: boolean;
}

let state = {
  isMiniCartOpen: false,
  isGiftPopupOpen: false,
};

// Set of listener functions to call when state changes
const listeners = new Set();

// Update type for listener function
type Listener = (state: CartState) => void;

// Function for components to subscribe to changes
const subscribe = (listener: Listener) => {
  listeners.add(listener);
  // Return an unsubscribe function
  return () => {
    listeners.delete(listener);
  };
};

// Notify all listeners that the state has changed
const notify = () => {
  listeners.forEach((listener) => listener(state));
};

// Internal function to update state and notify listeners
const updateState = (newState: Partial<CartState>) => {
  state = { ...state, ...newState };
  // Recalculate count and total based on items
  // Ensure price and quantity are numbers for calculation
  state.count = state.items?.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0
  ) || 0;
  state.total = state.items?.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.price || 0),
    0
  ) || 0;
  notify();
};

// --- Public API for the store ---

const getCartState = () => {
  return state;
};

// Fetches cart data from the server and updates the store state
const fetchCart = async () => {
  // Optional: Add loading/error state management to the store if needed
  // updateState({ loading: true, error: null });
  try {
    const response = await fetch("/cart.js");
    if (!response.ok) {
      // Try to get error details from response if possible
      let errorBody = "Failed to fetch cart";
      try {
        const errorData = await response.json();
        errorBody = errorData.description || errorData.message || errorBody;
      } catch (e) {
        /* Ignore JSON parsing error */
      }
      throw new Error(errorBody);
    }
    // Calculate custom properties more robustly
    let cartData = await response.json();
    const mainItems = cartData.items.filter(
      (item) => !item.properties?.is_custom_option
    );

    const mainItemsCount = mainItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    // Ensure 't' property exists and is treated as a number, default to 0 if missing/invalid
    const mainItemsComparedTotalCents = mainItems.reduce((sum, item) => {
      const originalItemPriceCents = Number(item.properties?.t || 0); // Get original price in cents from properties
      return sum + originalItemPriceCents * item.quantity;
    }, 0);

    // Update the main items total value.
    mainItems.forEach((item) => {
      const itemPrice = (Number(item.price || 0) / 100) * (Number(item.quantity || 0));
      const relatedItems = cartData.items.filter((loopItem) => {
        if (loopItem.properties?.is_custom_option) {
          return loopItem.properties?.custom_key === item.properties.custom_key;
        }
        return false;
      });
      let relatedItemsTotal = 0;
      if (relatedItems.length > 0) {
        relatedItemsTotal = relatedItems?.reduce((sum, relatedItem) => {
          return (
            sum +
            (Number(relatedItem.price || 0) / 100) *
              Number(relatedItem.quantity || 0)
          );
        }, 0);
      }
      const finalItemPrice = itemPrice + relatedItemsTotal;
      if (finalItemPrice) {
        cartData.items.find(
          (loopItem) => loopItem.key === item.key
        ).main_item_price = finalItemPrice.toFixed(2);
      }
    }, 0);

    // Convert cents to dollars for calculation
    const mainItemsTotal = mainItems.reduce((sum, mainItem) => {
      return sum + Number(mainItem.main_item_price || 0);
    }, 0);
    const mainItemsComparedTotal = mainItemsComparedTotalCents;

    // Calculate savings using the numeric values
    const mainItemsSavings = mainItemsComparedTotal - mainItemsTotal;

    // Shipping threshold
    const threshold = document.getElementById("shipping-bar-threshold").value;
    const belowThresholdMessage = document.getElementById(
      "shipping-bar-below-threshold-message"
    ).value;
    const metThresholdMessage = document.getElementById(
      "shipping-bar-met-threshold-message"
    ).value;

    const recommendationsCollection = document.getElementById(
      "cart-recommendations-collection"
    ).value;

    // Add custom properties to cartData, formatting to string with 2 decimals at the end
    cartData = {
      ...cartData,
      main_items_count: mainItemsCount,
      main_items_total: mainItemsTotal.toFixed(2),
      main_items_compared_total: mainItemsComparedTotal.toFixed(2),
      main_items_savings: mainItemsSavings.toFixed(2),
      free_shipping_threshold: threshold,
      free_shipping_below_threshold_message: belowThresholdMessage,
      free_shipping_met_threshold_message: metThresholdMessage,
      nvd_opted_out: cartData?.attributes?.nvd_opted_out || 0,
      recommendations_collection: recommendationsCollection,
      nvd_checkbox_state: true,
      nvd_checkbox_state_value: 6.00,
      gift_threshold_1:
        parseFloat(document.getElementById("cart-gift-threshold-1").value) /
        100,
      gift_threshold_2:
        parseFloat(document.getElementById("cart-gift-threshold-2").value) /
        100,
      gift_threshold_3:
        parseFloat(document.getElementById("cart-gift-threshold-3").value) /
        100,
    };

    // Update the store's items with the fetched data
    // updateState will recalculate count and total
    updateState(cartData);
  } catch (err) {
    // console.error("Error fetching cart:", err);
    // Optional: Update store state with error
    // updateState({ error: err.message, loading: false });
    // Decide how to handle the items state on error, e.g., clear it or keep previous
    // updateState({ items: [] }); // Example: Clear items on error
  }
};

const addItem = (itemToAdd) => {
  const existingItemIndex = state.items.findIndex(
    (item) => item.id === itemToAdd.id
  );
  let newItems = [...state.items];

  if (existingItemIndex > -1) {
    // Update quantity if item exists
    newItems[existingItemIndex] = {
      ...newItems[existingItemIndex],
      quantity:
        newItems[existingItemIndex].quantity + (itemToAdd.quantity || 1),
    };
  } else {
    // Add new item
    newItems.push({ ...itemToAdd, quantity: itemToAdd.quantity || 1 });
  }
  fetchCart();
};

// Adds a gift item, ensuring only one item with _isFreeGift property exists
const addGiftItem = async (itemToAdd) => {
  const newGiftVariantId = itemToAdd.variants[0].id;

  // Fetch current cart state to check for existing gifts accurately
  // Ensure local state is up-to-date before checking
  await fetchCart();
  const currentState = getCartState(); // Get the latest state after fetch

  // Find the existing free gift item, if any
  const existingGift = currentState.items.find(
    (item) => item.properties?._isFreeGift === true // Check for the specific property
  );

  // 1. Remove existing gift if it's different from the new one
  if (existingGift && existingGift.variant_id !== newGiftVariantId) {
    try {
      const removeResponse = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        // Use the line item key and set quantity to 0 for removal
        body: JSON.stringify({ updates: { [existingGift.key]: 0 } }),
      });

      if (!removeResponse.ok) {
        // Log error but attempt to continue adding the new gift
        console.error("Failed to remove existing gift item. Status:", removeResponse.status);
        // Optionally parse error body for more details
      }
      // Fetch cart again after removal attempt to ensure state consistency before adding
      await fetchCart();
    } catch (err) {
      console.error("Error removing existing gift item:", err);
      // Decide how to handle this error - perhaps stop the process?
      // For now, we log and continue.
    }
  } else if (existingGift && existingGift.variant_id === newGiftVariantId) {
    console.log("Gift item already in cart.");
    // The desired gift is already present, no action needed.
    // Fetch cart just to ensure state is consistent if needed elsewhere.
    await fetchCart();
    return;
  }

  // 2. Add the new gift item using /cart/add.js
  try {
    const addResponse = await fetch("/cart/add.js", { // Use /cart/add.js
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      // Send item details including id, quantity, and properties
      body: JSON.stringify({
        items: [
          {
            id: newGiftVariantId,
            quantity: 1,
            properties: {
              t: itemToAdd.variants[0].compare_at_price,
              _isFreeGift: true, // Add the property here
            },
          },
        ],
      }),
    });

    if (!addResponse.ok) {
      let errorBody = `Failed to add gift item to cart`;
      try {
        const errorData = await addResponse.json();
        errorBody = errorData.description || errorData.message || errorBody;
      } catch (e) { /* Ignore JSON parsing error */ }
      throw new Error(errorBody);
    }

    // If the API call is successful, fetch the updated cart state
    await fetchCart();

  } catch (err) {
    console.error("Error adding gift item:", err);
    // Optional: Update store state with error
    // updateState({ error: err.message, loading: false });
  }
  // Optional: Add final loading state update here if implemented
  // updateState({ loading: false });
};

// Remove an item from the cart

const removeItem = async (itemIdOrIds: string | string[]): Promise<void> => {
  const idsToRemove: string[] = Array.isArray(itemIdOrIds) ? itemIdOrIds : [itemIdOrIds];
  const updates: Record<string, number> = {};

  idsToRemove.forEach((itemId: string) => {
    const itemToRemove = state.items.find((item: CartItem) => item.key === itemId);

    if (itemToRemove) {
      // Use the item's key directly without conversion
      updates[itemId] = 0;

      const customKey = itemToRemove.properties?.custom_key;
      if (customKey) {
        state.items.forEach((item: CartItem) => {
          if (
            item.properties?.is_custom_option &&
            item.properties?.custom_key === customKey
          ) {
            updates[item.key] = 0;
          }
        });
      }
    } else {
      console.warn(
        `Item with key ${itemId} not found in cart state during removal.`
      );
    }
  });

  if (Object.keys(updates).length === 0) {
    console.log("No items found matching the provided keys to remove.");
    return;
  }

  try {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        updates: updates // Remove the Object.fromEntries conversion
      }),
    });

    if (!response.ok) {
      let errorBody = `Failed to remove items from cart`;
      try {
        const errorData = await response.json();
        errorBody = errorData.description || errorData.message || errorBody;
      } catch (e) {
        /* Ignore JSON parsing error if response body is not valid JSON */
      }
      throw new Error(errorBody);
    }

    await fetchCart();
  } catch (err) {
    console.error("Error removing items:", err);
  }
};

// Update the quantity of an item and its related items in the cart by calling the Shopify API
const updateQuantity = async (itemId, quantity, customKey) => {
  const newQuantity = Math.max(0, quantity); // Ensure quantity is not negative

  // Prepare the updates object for the API call
  const updates = {};
  updates[itemId] = newQuantity; // Add the main item to the updates

  // Find related items based on the custom_key property matching the main item's ID (key)
  const relatedItems = state.items.filter((item: CartItem) => {
    if (item.properties?.is_custom_option) {
      return item.properties?.custom_key === customKey;
    }
    return false;
  });
  relatedItems.forEach((item: CartItem) => {
    updates[item.key] = newQuantity; // Add related items to the updates
  });

  try {
    // Call the Shopify Cart API to update item quantities
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      let errorBody = `Failed to update quantity for item ${itemId} and related items`;
      try {
        const errorData = await response.json();
        errorBody = errorData.description || errorData.message || errorBody;
      } catch (e) {
        /* Ignore JSON parsing error */
      }
      throw new Error(errorBody);
    }

    // If the API call is successful, fetch the updated cart state
    await fetchCart();
  } catch (err) {
    console.error("Error updating item quantities:", err);
  }
};

const clearCart = () => {
  // This should likely also call an API endpoint, e.g., /cart/clear.js
  // For now, it only clears local state
  updateState({ items: [] });
  // Example API call (if endpoint exists):
  // fetch('/cart/clear.js', { method: 'POST' }).then(() => fetchCart());
};

// Update function signatures with proper typing
const openMiniCart = (): void => {
  updateState({ isMiniCartOpen: true });
};

const closeMiniCart = (): void => {
  updateState({ isMiniCartOpen: false });
};

const isMiniCartOpen = (): boolean => {
  return state.isMiniCartOpen;
};

const isGiftPopupOpen = (): boolean => {
  return state.isGiftPopupOpen;
};

const openGiftPopup = (): void => {
  updateState({ isGiftPopupOpen: true });
};

const closeGiftPopup = (): void => {
  updateState({ isGiftPopupOpen: false });
};

const isAddingGift = (value: boolean): void => {
  updateState({ isAddingGift: value });
};

const updateNvdCheckboxState = (state: boolean): void => {
  updateState({ nvd_checkbox_state: state });
};

const updateNvdCheckboxStateValue = (value: number): void => {
  updateState({ nvd_checkbox_state_value: value });
};

// Export the public API
export const cartStore = {
  getCartState,
  fetchCart,
  addItem,
  removeItem,
  updateQuantity,
  clearCart,
  subscribe,
  openMiniCart,
  closeMiniCart,
  isMiniCartOpen,
  addGiftItem,
  openGiftPopup,
  closeGiftPopup,
  isGiftPopupOpen,
  updateNvdCheckboxState,
  updateNvdCheckboxStateValue,
  isAddingGift,
};

// Fetch cart data when the module is first loaded
fetchCart();
